import React from "react";
import { Box, Paper, Typography, Button, Alert } from "@mui/material";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import QrCodeScannerIcon from "@mui/icons-material/QrCodeScanner";
import PhoneIcon from "@mui/icons-material/Phone";

const QrCodeNotFoundError = ({ scannedCode, onTryAgain }) => {
  const supportPhone = "03-9371188";
  const supportEmail = "ls-serv@eldan.biz";

  const handleCallSupport = () => {
    window.location.href = `tel:${supportPhone}`;
  };

  const handleEmailSupport = () => {
    const subject = encodeURIComponent("בעיה בסריקת QR Code");
    const body = encodeURIComponent(
      `שלום,\n\nסרקתי קוד QR שלא נמצא במערכת.\n\nקוד שנסרק: ${scannedCode || "לא זמין"}\n\nאנא עזרו לי לפתור את הבעיה.\n\nתודה.`
    );
    window.location.href = `mailto:${supportEmail}?subject=${subject}&body=${body}`;
  };

  return (
    <Paper
      sx={{
        p: 3,
        borderRadius: "12px",
        boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
        mt: 2,
        mb: 2,
      }}
      dir="rtl"
    >
      <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 2 }}>
        {/* Error Icon */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            width: 80,
            height: 80,
            borderRadius: "50%",
            backgroundColor: "#ffebee",
            mb: 1,
          }}
        >
          <ErrorOutlineIcon sx={{ fontSize: 48, color: "#d32f2f" }} />
        </Box>

        {/* Error Title */}
        <Typography
          variant="h5"
          sx={{
            fontFamily: "Rubik, sans-serif",
            fontWeight: 600,
            color: "#d32f2f",
            textAlign: "center",
          }}
        >
          קוד QR לא נמצא
        </Typography>

        {/* Error Message */}
        <Alert severity="error" sx={{ width: "100%", fontFamily: "Rubik, sans-serif" }}>
          <Typography variant="body1" sx={{ fontFamily: "Rubik, sans-serif" }}>
            קוד QR שסרקת לא נמצא במערכת. אנא פנה לשירות לקוחות לקבלת סיוע.
          </Typography>
        </Alert>

        {/* Scanned Code Display (if available) */}
        {scannedCode && (
          <Box
            sx={{
              width: "100%",
              p: 2,
              backgroundColor: "#f5f5f5",
              borderRadius: "8px",
              border: "1px solid #e0e0e0",
            }}
          >
            <Typography
              variant="body2"
              sx={{
                fontFamily: "Rubik, sans-serif",
                color: "#666",
                textAlign: "center",
              }}
            >
              <strong>הקוד שנסרק:</strong> {scannedCode}
            </Typography>
          </Box>
        )}

        {/* Instructions */}
        <Box sx={{ width: "100%", mt: 2 }}>
          <Typography
            variant="body2"
            sx={{
              fontFamily: "Rubik, sans-serif",
              color: "#666",
              mb: 1,
              textAlign: "center",
            }}
          >
            מה אפשר לעשות?
          </Typography>
          <Box
            component="ul"
            sx={{
              fontFamily: "Rubik, sans-serif",
              fontSize: "0.875rem",
              color: "#666",
              lineHeight: 1.8,
              pr: 2,
            }}
          >
            <li>וודא/י שסרקת את הקוד הנכון</li>
            <li>נסה/י לסרוק שוב את קוד ה-QR</li>
            <li>צור/י קשר עם שירות הלקוחות לקבלת עזרה</li>
          </Box>
        </Box>

        {/* Action Buttons */}
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5, width: "100%", mt: 2 }}>
          {/* Email Support Button - Primary */}
          <Button
            fullWidth
            variant="contained"
            onClick={handleEmailSupport}
            sx={{
              backgroundColor: "#006699",
              borderRadius: "8px",
              padding: "12px",
              fontFamily: "Rubik, sans-serif",
              fontSize: "1rem",
              fontWeight: 500,
              "&:hover": {
                backgroundColor: "#005588",
              },
            }}
          >
            שלח מייל לתמיכה
          </Button>

          {/* Call Support Button */}
          <Button
            fullWidth
            variant="outlined"
            startIcon={<PhoneIcon />}
            onClick={handleCallSupport}
            sx={{
              borderColor: "#006699",
              color: "#006699",
              borderRadius: "8px",
              padding: "12px",
              fontFamily: "Rubik, sans-serif",
              fontSize: "1rem",
              fontWeight: 500,
              "&:hover": {
                borderColor: "#005588",
                backgroundColor: "#f0f8ff",
              },
            }}
          >
            התקשר לשירות לקוחות
          </Button>

          {/* Try Again Button - Go back to login */}
          {onTryAgain && (
            <Button
              fullWidth
              variant="text"
              startIcon={<QrCodeScannerIcon />}
              onClick={onTryAgain}
              sx={{
                color: "#006699",
                borderRadius: "8px",
                padding: "12px",
                fontFamily: "Rubik, sans-serif",
                fontSize: "0.875rem",
                "&:hover": {
                  backgroundColor: "#f0f8ff",
                },
              }}
            >
              נסה שוב
            </Button>
          )}
        </Box>

        {/* Support Info */}
        <Box
          sx={{
            width: "100%",
            mt: 3,
            pt: 2,
            borderTop: "1px solid #e0e0e0",
          }}
        >
          <Typography
            variant="caption"
            sx={{
              fontFamily: "Rubik, sans-serif",
              color: "#999",
              textAlign: "center",
              display: "block",
            }}
          >
            מייל: ls-serv@eldan.biz
          </Typography>
          <Typography
            variant="caption"
            sx={{
              fontFamily: "Rubik, sans-serif",
              color: "#999",
              textAlign: "center",
              display: "block",
              mt: 0.5,
            }}
          >
            טלפון: 03-9371188
          </Typography>
        </Box>
      </Box>
    </Paper>
  );
};

export default QrCodeNotFoundError;
